import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import authService from '../services/firebase/authService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [firebaseUser, setFirebaseUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            try {
                if (fbUser) {
                    setFirebaseUser(fbUser);

                    // Get token
                    const idToken = await fbUser.getIdToken();
                    localStorage.setItem('firebaseToken', idToken);

                    // Always fetch fresh profile from Firestore (UID-based lookup)
                    // Do NOT pre-load localStorage — it may contain a different user's data
                    // from a previous session (causes wrong employee to appear on login).
                    try {
                        const freshUser = await authService.fetchUserData(fbUser);
                        if (freshUser) {
                            setUser(freshUser);
                            setIsAuthenticated(true);
                        }
                    } catch (fetchErr) {
                        console.warn('[AuthContext] fetchUserData failed, falling back to localStorage:', fetchErr);
                        // Only use localStorage as last resort fallback
                        const storedUser = authService.getUser();
                        if (storedUser && storedUser.uid === fbUser.uid) {
                            setUser(storedUser);
                            setIsAuthenticated(true);
                        }
                    }

                } else {
                    setFirebaseUser(null);
                    setUser(null);
                    setIsAuthenticated(false);
                    localStorage.removeItem('firebaseToken');
                    localStorage.removeItem('user');
                }
            } catch (error) {
                console.error('Auth state change error:', error);
                setFirebaseUser(null);
                setUser(null);
                setIsAuthenticated(false);
            } finally {
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);


    const login = async (email, password) => {
        try {
            const response = await authService.login(email, password);
            setUser(response.data.user);
            setIsAuthenticated(true);
            return response;
        } catch (error) {
            throw error;
        }
    };

    const register = async (userData) => {
        try {
            const response = await authService.register(userData);
            return response;
        } catch (error) {
            throw error;
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
            setUser(null);
            setIsAuthenticated(false);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const value = {
        user,
        firebaseUser,
        loading,
        isAuthenticated,
        login,
        register,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
